import { GLOBAL_DATA_STATE, LOCAL_DATA_STATE } from '@/store/constants';

import { convertOsisToUsfm, convertUsfmToOsis, parseOsisRef } from '@/common/refUtils';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from '@/symphony/shared/languages';

/**
 * Used to make a widget's inputs available as computed properties.
 * Currently requires fields to be declared via doricConfiguration.dataFields
 */
const InputDataMixin = {
  props: {
    info: Object,
  },
  methods: {
    getDataFieldValue(variableName) {
      let value;
      const inputsContext = this.info.inputs;
      const groupKeyFromInput = inputsContext[variableName]?.groupKey;

      const workspace = this.$store.getters.getActiveWorkspace;
      if (!workspace) {
        return null;
      }

      if (groupKeyFromInput === GLOBAL_DATA_STATE) {
        // If the variableName is 'global', then return the global data
        value = workspace.globalFields[inputsContext[variableName].value];
      } else if (groupKeyFromInput === LOCAL_DATA_STATE) {
        // If the variableName is 'local', then return the local data
        value = inputsContext[variableName]?.value;
      } else {
        // Otherwise, use the variableName to access the correct variable group
        value = workspace.variableGroups.find(group => group.key === groupKeyFromInput)?.variables[
          inputsContext[variableName]?.value
        ];
      }
      return value;
    },
  },
  beforeCreate() {
    const computed = {
      ...this.$options.computed,
    };
    const doricInputs = this.info?.inputs;
    if (doricInputs) {
      Object.keys(doricInputs).forEach(inputFieldKey => {
        computed[inputFieldKey] = () => this.getDataFieldValue(inputFieldKey);
      });
    }
    this.$options.computed = computed;
  },
};

const OutputsMixin = {
  props: {
    info: Object,
  },
  data() {
    return {
      outputs: { ...(this.info?.outputs || {}) },
    };
  },
  methods: {
    submit() {
      // TODO: What kind of a contract do we want on inputs?
      const inputs = this.info?.inputs || {};
      this.$emit('configuration-update', { inputs, outputs: this.outputs });
    },
  },
};

const RefAdapterMixin = {
  computed: {
    usfmRef() {
      return convertOsisToUsfm(this.osisRef);
    },
    parsedOsisRef() {
      return parseOsisRef(this.osisRef);
    },
    contextualizedOsisRef() {
      return `${this.parsedOsisRef.book}.${this.parsedOsisRef.chapter ?? 1}`;
    },
    contextualizedUsfmRef() {
      return convertOsisToUsfm(this.contextualizedOsisRef);
    },
  },
  methods: {
    toOsisRef(value) {
      // TODO: Improve inclusion / exclusion
      // of tokens
      const [ref, token] = value.split('!');
      const osisRef = convertUsfmToOsis(ref);
      return `${osisRef}!${token}`;
    },
  },
};

/**
 * LocalizationMixin provides language support for parameterized GraphQL fields.
 * Reads the language preference from URL query parameters and provides
 * methods to work with GraphQL localization data.
 *
 * The GraphQL schema provides:
 * - Parameterized fields: `definition(language: String, allowFallback: Boolean): String`
 * - Localizations array: `localizations: [{ language, definition, notes, examples }]`
 *
 * Usage:
 * ```javascript
 * export default {
 *   mixins: [LocalizationMixin],
 *   apollo: {
 *     keyTerm: {
 *       query: gql`{
 *         keyTerm(id: $id) {
 *           definition(language: $lang)
 *           localizations { language, definition, notes, examples }
 *         }
 *       }`,
 *       variables() {
 *         return { id: this.termId, lang: this.lang };
 *       }
 *     }
 *   },
 *   methods: {
 *     checkFallback() {
 *       const isExactMatch = this.isLocalizedLanguageAvailable(this.keyTerm.localizations);
 *       if (!isExactMatch) {
 *         console.log('Showing English fallback');
 *       }
 *     }
 *   }
 * }
 * ```
 */
const LocalizationMixin = {
  data() {
    return {
      availableLanguages: AVAILABLE_LANGUAGES,
    };
  },
  computed: {
    /**
     * Get the current language code from URL query parameter.
     * Falls back to DEFAULT_LANGUAGE if not specified.
     * Can be used with v-model for two-way binding to update URL.
     * @returns {string} ISO 639-3 language code (e.g., 'eng', 'fra', 'spa')
     */
    lang: {
      get() {
        return this.$route?.query?.lang || DEFAULT_LANGUAGE;
      },
      set(newValue) {
        const query = {
          ...this.$route.query,
          lang: newValue,
        };
        this.$router.replace({ query });
      },
    },
  },
  methods: {
    /**
     * Check if an exact match exists for the current language in localizations.
     * Use to detect if a parameterized field returned a fallback value.
     *
     * @param {Array} localizations - Array of { language, definition, ... } objects from GraphQL
     * @returns {boolean} True if current language has an exact match
     *
     * @example
     * // Detect fallback usage
     * const isExactMatch = this.isLocalizedLanguageAvailable(keyTerm.localizations);
     * if (!isExactMatch) {
     *   console.log('Showing English fallback');
     * }
     */
    isLocalizedLanguageAvailable(localizations) {
      if (!Array.isArray(localizations)) {
        return false;
      }
      return localizations.some(localization => localization.language === this.lang);
    },

    /**
     * Find a localization entry by language code.
     *
     * @param {Array} localizations - Array of localization objects from GraphQL
     * @param {string} language - ISO 639-3 language code (defaults to current language)
     * @returns {Object|null} Localization object with { language, definition, notes, examples }
     *
     * @example
     * const frenchVersion = this.getLocalizationByLanguage(keyTerm.localizations, 'fra');
     * if (frenchVersion) {
     *   console.log(frenchVersion.definition);
     * }
     */
    getLocalizationByLanguage(localizations, language = this.lang) {
      if (!Array.isArray(localizations)) {
        return null;
      }
      return localizations.find(localization => localization.language === language) || null;
    },

    /**
     * Get all available language codes from localizations array.
     *
     * @param {Array} localizations - Array of localization objects from GraphQL
     * @returns {Array<string>} Array of ISO 639-3 language codes
     *
     * @example
     * const availableLanguages = this.getAvailableLanguages(keyTerm.localizations);
     * // ['eng', 'fra', 'spa']
     */
    getAvailableLanguages(localizations) {
      if (!Array.isArray(localizations)) {
        return [];
      }
      return localizations.map(localization => localization.language);
    },

    /**
     * Get a localized field value with fallback to default language.
     * Works with the new parameterized GraphQL schema where fields like `title(language)`
     * are already fetched with the requested language.
     *
     * @param {Object} obj - Object containing the field to retrieve
     * @param {string} fieldName - Name of the field (e.g., 'title', 'description', 'content')
     * @returns {string|null} The field value in requested language or fallback to English, null if not found
     *
     * @example
     * // GraphQL returns title(language) and localizations array
     * const article = {
     *   title: "Titre en Français",  // Already fetched with lang=fra
     *   description: "Description française",
     *   localizations: [
     *     { language: 'fra', title: 'Titre en Français', description: 'Description française' },
     *     { language: 'eng', title: 'French Title', description: 'French Description' }
     *   ]
     * };
     *
     * // Get localized field with fallback
     * const title = this.getLocalizedOrFallback(article, 'title');  // "Titre en Français"
     * const desc = this.getLocalizedOrFallback(article, 'description');  // "Description française"
     */
    getLocalizedOrFallback(obj, fieldName) {
      if (!obj) {
        return null;
      }

      // First try the direct parameterized field (already fetched with current language)
      if (obj[fieldName]) {
        return obj[fieldName];
      }

      // If not found, try to get from localizations array
      if (Array.isArray(obj.localizations)) {
        const localization = this.getLocalizationByLanguage(obj.localizations, this.lang);
        if (localization && localization[fieldName]) {
          return localization[fieldName];
        }

        // If current language not found, fallback to default (English)
        const fallback = this.getLocalizationByLanguage(obj.localizations, DEFAULT_LANGUAGE);
        if (fallback && fallback[fieldName]) {
          return fallback[fieldName];
        }
      }

      return null;
    },
  },
};

export default { InputDataMixin, OutputsMixin, RefAdapterMixin, LocalizationMixin };
